<template>
  <v-container>
    <h1 class="mb-5">Admin</h1>

    <v-form class="pb-10">
      <v-row>
        <v-col>
          <h2>Festival Dates</h2>
          <h1 v-if="globalSettings && globalSettings.festivalDates">
            {{ festivalStart }} to {{ festivalEnd }}
          </h1>
          <v-date-picker
            v-if="globalSettings"
            v-model="globalSettings.festivalDates"
            range
            full-width
          />
        </v-col>
      </v-row>
      <v-btn large class="accent" @click="updateFestivalDates">Save Dates</v-btn>
    </v-form>

    <v-form class="pb-10">
      <v-card>
        <v-card-title>Assign Admin</v-card-title>
        <v-card-text>Admin users can view and edit all authors and events, and assign new admin users.</v-card-text>
        <v-card-text>
          <v-text-field
            v-model="newAdminEmail"
            label="Email"
            type="email"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn @click="assignAdmin" large color="accent">Assign</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <v-form class="pb-10">
      <v-card>
        <v-card-title>Assign Viewer</v-card-title>
        <v-card-text>Viewers can view all authors and schedules.</v-card-text>
        <v-card-text>
          <v-text-field
            v-model="newViewerEmail"
            label="Email"
            type="email"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn @click="assignViewer" large color="accent">Assign</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <v-form class="pb-10">
      <v-card>
        <v-card-title>Support Email</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="globalSettings.supportEmail"
            label="Email"
            type="email"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn @click="updateSupportEmail" large color="accent">Set Support Email</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <TheAuthorsTable />
    <TheAmbassadorsTable />

    <h2>Existing Locations
      <v-btn color="primary" @click="$root.$emit('showLocationEditor')">
        New Location
        <v-icon right>mdi-plus</v-icon>
      </v-btn>
    </h2>
    <TheLocations />

    <h2>Existing Hotels
      <v-btn color="primary" @click="$root.$emit('showHotelEditor')">
        New Hotel
        <v-icon right>mdi-plus</v-icon>
      </v-btn>
    </h2>
    <TheHotels />

    <h2>Existing Event Categories
      <v-btn color="primary" @click="$root.$emit('showEventCategoryEditor')">
        New Event Category
        <v-icon right>mdi-plus</v-icon>
      </v-btn>
    </h2>
    <TheEventCategories />

    <div class="pb-10">
      <h2>Archive</h2>
      <v-btn color="error" @click="showArchiveModal">Archive and Clear System<v-icon right>mdi-archive</v-icon></v-btn>

      <h3 class="mt-5">Existing Archives</h3>
      <div v-if="allArchives && allArchives.length > 0">
        <v-card v-for="archive in allArchives" :key="archive.id" class="mb-3" :loading="deletingArchiveId === archive.id">
          <v-card-title class="pb-2">
            <v-icon left>mdi-archive</v-icon>
            {{ archive.id }}
            <v-spacer></v-spacer>
          </v-card-title>
          <v-card-text class="pt-0">
            <div class="text--secondary">
              <div><strong>Created:</strong> {{ formatArchiveDate(archive.created) }}</div>
              <div v-if="archive.initiatedBy"><strong>Initiated by:</strong> {{ archive.initiatedBy }}</div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="primary"
              :disabled="deletingArchiveId === archive.id"
              @click="showRestoreModal(archive.id)"
            >
              <v-icon left>mdi-restore</v-icon>
              Restore
            </v-btn>
            <v-btn
              color="error"
              :disabled="deletingArchiveId === archive.id"
              :loading="deletingArchiveId === archive.id"
              @click="deleteArchive(archive.id)"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
      <div v-else-if="allArchives && allArchives.length === 0" class="text--secondary mt-3">
        No archives found.
      </div>
      <div v-else class="text--secondary mt-3">
        Loading archives...
      </div>
    </div>
    <TheArchiveModal :is-restore-mode="!!selectedArchiveName" :archive-name="selectedArchiveName" />

    <h2>Developer</h2>
    <v-btn v-if="!showingDeveloperFunctions" color="error" @click="showingDeveloperFunctions = true">Show Functions</v-btn>
    <v-btn v-if="showingDeveloperFunctions" color="primary" @click="showingDeveloperFunctions = false">Hide Functions</v-btn>
    <br><br>
    <template v-if="showingDeveloperFunctions">
      <v-btn color="error" @click="exportRsvpData">Export RSVP Data (see source code for details)</v-btn><br><br>

      <v-btn color="error" @click="clearNewlines">Clear \n from Author Names</v-btn>
    </template>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { setDoc, updateDoc, doc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import TheAuthorsTable from '@/components/TheAuthorsTable'
import TheAmbassadorsTable from '@/components/TheAmbassadorsTable'
import TheLocations from '@/components/TheLocations'
import TheHotels from '@/components/TheHotels'
import TheEventCategories from '@/components/TheEventCategories'
import TheArchiveModal from '@/components/TheArchiveModal.vue'

export default {
  components: {
    TheAuthorsTable,
    TheAmbassadorsTable,
    TheLocations,
    TheHotels,
    TheEventCategories,
    TheArchiveModal
  },
  async created () {
    this.globalFlagsListener()
    await this.authorsListener()
    await this.ambassadorsListener()
    await this.archivesListener()
  },
  data: () => ({
    newAdminEmail: null,
    newViewerEmail: null,
    showingDeveloperFunctions: false,
    selectedArchiveName: '',
    deletingArchiveId: null
  }),
  computed: {
    ...mapState(['firestore', 'fbFunctions', 'globalSettings', 'allArchives']),
    ...mapGetters(['allAuthors', 'allLocations', 'myEvents']),
    festivalStart () {
      return this.globalSettings?.festivalDates?.length ? this.formatIsoDate(this.globalSettings.festivalDates[0]) : ''
    },
    festivalEnd () {
      return this.globalSettings?.festivalDates?.length ? this.formatIsoDate(this.globalSettings.festivalDates[this.globalSettings.festivalDates.length - 1]) : ''
    }
  },
  methods: {
    ...mapMutations(['setGlobalNotification']),
    ...mapActions(['authorsListener', 'ambassadorsListener', 'globalFlagsListener', 'archivesListener']),

    async updateFestivalDates () {
      console.log('Updating festival dates:', this.globalSettings.festivalDates)
      try {
        await setDoc(doc(this.firestore, 'settings', 'global'), {
          festivalDates: this.globalSettings.festivalDates
        }, { merge: true })
        this.setGlobalNotification({ val: 'Updated festival dates.' })
      } catch (err) {
        console.log(err)
        this.setGlobalNotification({ val: 'Error updating festival dates.', type: 'error' })
      }
    },

    /**
     * Assigns Firebase admin privileges to the target email address.
     */
    async assignAdmin () {
      console.log('Assigning admin privileges to', this.newAdminEmail)
      try {
        await httpsCallable(this.fbFunctions, 'assignAdmin')(this.newAdminEmail)
        this.setGlobalNotification(`Assigned admin privileges to ${this.newAdminEmail}`)
      } catch (err) {
        console.log(err)
        this.setGlobalNotification({ val: err.message, type: 'error' })
      }
    },

    /**
     * Assigns Firebase "viewer" privileges to the target email address.
     */
    async assignViewer () {
      console.log('Assigning viewer privileges to', this.newViewerEmail)
      try {
        await httpsCallable(this.fbFunctions, 'assignViewer')(this.newViewerEmail)
        this.setGlobalNotification(`Assigned viewer privileges to ${this.newViewerEmail}`)
      } catch (err) {
        console.log(err)
        this.setGlobalNotification({ val: err.message, type: 'error' })
      }
    },

    async updateSupportEmail () {
      console.log('Updating support email:', this.globalSettings.supportEmail)
      if (!this.globalSettings.supportEmail) {
        this.setGlobalNotification({ val: 'Please enter a valid email address.', type: 'error' })
        return
      }
      try {
        await setDoc(doc(this.firestore, 'settings', 'global'), {
          supportEmail: this.globalSettings.supportEmail
        }, { merge: true })
        this.setGlobalNotification({ val: 'Updated support email.' })
      } catch (err) {
        console.log(err)
        this.setGlobalNotification({ val: 'Error updating festival dates.', type: 'error' })
      }
    },

    exportRsvpData () {
      const targetEvent = 'iqjd9QxgscqMIHCJTRf8'
      // const targetEvent = 'pa1142vIHAyrR0s0gKmP'
      const targetEventData = this.myEvents.find((event) => event.id === targetEvent)
      const { rsvps, plusOneRsvps } = targetEventData

      let rsvpDataFormatted = 'Name, Email, RSVP, Plus One\n'
      Object.keys(rsvps).forEach((key) => {
        if (rsvps[key] === 'yes') {
          const targetAuthorData = this.allAuthors.find((author) => author.id === key)

          let emailsFormatted = ''
          if (targetAuthorData?.associatedEmails && targetAuthorData?.associatedEmails?.length) {
            targetAuthorData.associatedEmails.forEach((email, index) => {
              email.replace(/,/g, '')
              if (index > 0 && index < targetAuthorData.associatedEmails.length) {
                emailsFormatted += ' ' // add space after the first entry
              }
              emailsFormatted += email
            })
          }
          const nameFormatted = targetAuthorData?.fullName?.replace(/,/g, '')
          rsvpDataFormatted += `${nameFormatted || ''}, ${emailsFormatted || ''}, ${rsvps[key] || ''}, ${plusOneRsvps[key] || ''}\n`
        }
      })

      // Now convert to file to download.
      const blob = new Blob([rsvpDataFormatted], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'rsvps.csv'
      link.click()
    },

    /**
     * This utility function loops through all of the authors pulled from Firestore
     * and removes any newlines or extra whitespace from their names, which might exist
     * if the data was imported from a CSV.
     */
    async clearNewlines () {
      if (!confirm('Are you sure you want to clear newlines from author names? This cannot be undone.')) return
      const authors = this.allAuthors
      for (let i = 0; i < authors.length; i++) {
        const author = authors[i]
        const authorBefore = JSON.parse(JSON.stringify(author))
        author.firstName = author.firstName?.replaceAll('\n', '').trim()
        author.lastName = author.lastName?.replaceAll('\n', '').trim()
        author.fullName = `${author.firstName} ${author.lastName}`

        const nameData = {
          firstName: author.firstName || 'FirstName',
          lastName: author.lastName || 'LastName',
          fullName: author.fullName || 'FirstName LastName'
        }
        await updateDoc(doc(this.firestore, 'authors', author.id), nameData)

        if (authorBefore.fullName !== author.fullName || authorBefore.firstName !== author.firstName || authorBefore.lastName !== author.lastName) {
          console.log('Updated', author.fullName)
        }
      }
      this.setGlobalNotification({ val: 'Cleared newlines from author names.' })
    },

    formatArchiveDate (timestamp) {
      if (!timestamp) return 'Unknown'
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString()
    },

    showArchiveModal () {
      this.selectedArchiveName = ''
      this.$root.$emit('showArchiveRestoreModal')
    },

    showRestoreModal (archiveName) {
      this.selectedArchiveName = archiveName
      this.$root.$emit('showArchiveRestoreModal')
    },

    async deleteArchive (archiveName) {
      const userInput = prompt(`To confirm deletion of archive "${archiveName}", please type the archive name exactly:`)
      if (userInput === archiveName) {
        this.deletingArchiveId = archiveName
        try {
          await httpsCallable(this.fbFunctions, 'deleteArchive')(archiveName)
          this.setGlobalNotification({
            val: `Archive "${archiveName}" deleted successfully.`,
            type: 'success'
          })
        } catch (err) {
          console.error(err)
          this.setGlobalNotification({
            val: 'Error deleting archive: ' + (err.message || 'Unknown error'),
            type: 'error'
          })
        } finally {
          this.deletingArchiveId = null
        }
      } else if (userInput !== null) {
        alert(`Archive deletion cancelled. You must type "${archiveName}" exactly to proceed.`)
      }
    }
  }
}
</script>

<style>

</style>
